import React, {Fragment} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Loader2, Pencil, Plus, Trash2, X} from 'lucide-react';
import {HEALTH_CATEGORY_LABELS} from '@mld/shared/constants/biomarkers';
import {toast} from 'react-toastify';
import clsx from 'clsx';
import AdminService from '@/modules/admin/services/adminService';
import LoincLookupField from '@/modules/admin/components/LoincLookupField';
import {validateDisplayRule} from '@/shared/biomarkers/validateDisplayRule';
import {ruleToConfig, bandColour} from '@/shared/biomarkers/ruleToConfig';
import type {
    BandColourKey,
    BiomarkerDisplayRule,
    DisplayBand,
    DisplayMode,
    RuleSex,
    ValidationError,
} from '@/shared/biomarkers/displayRuleTypes';
import type {BiomarkerDisplayRuleInput, UnmappedAnalyteSuggestion} from '@/shared/types/api';

const QUERY_KEY = ['admin', 'biomarker-display-rules'];
/** The Needs mapping tab's list — a saved rule can remove rows from it. */
export const UNMAPPED_QUERY_KEY = ['admin', 'unmapped-analytes'];

const SEX_OPTIONS: {value: RuleSex; label: string}[] = [
    {value: 'any', label: 'Any'},
    {value: 'male', label: 'Male'},
    {value: 'female', label: 'Female'},
];

const SEX_LABEL: Record<RuleSex, string> = {any: 'Any', male: 'Male', female: 'Female'};

/** Options come straight from the shared registry's label map so this select
 *  can never offer a bucket the API vocabulary rejects. */
const CATEGORY_OPTIONS = Object.entries(HEALTH_CATEGORY_LABELS) as [string, string][];

const MODE_OPTIONS: {value: DisplayMode; label: string}[] = [
    {value: 'banded', label: 'Banded (chart)'},
    {value: 'value_only', label: 'Value only (text)'},
];

/** The bar exactly as a patient sees it — same adapter, same colors. An admin
 *  must never have to imagine what a severity choice looks like. */
const BandPreview: React.FC<{rule: BiomarkerDisplayRule}> = ({rule}) => {
    const config = ruleToConfig(rule);
    if (!config) {
        return <span className="text-xs text-gray-500">Value only — no chart</span>;
    }
    return (
        <div className="flex h-2 w-40 overflow-hidden rounded-full" role="img" aria-label="Band preview">
            {config.zones.map((z, i) => (
                <div key={i} style={{width: `${z.percentage}%`, backgroundColor: z.color}} title={z.label} />
            ))}
        </div>
    );
};

/** Live validation with the SAME function the API and the seed script use, so
 *  the UI can never accept something the server will reject. */
function useBandErrors(
    displayMode: DisplayMode,
    referenceText: string | null,
    bands: DisplayBand[]
): ValidationError[] {
    return React.useMemo(
        () => validateDisplayRule({display_mode: displayMode, reference_text: referenceText, bands}),
        [displayMode, referenceText, bands]
    );
}

const inputClass =
    'rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary';

const BandRow: React.FC<{
    band: DisplayBand;
    index: number;
    errors: ValidationError[];
    onChange: (next: DisplayBand) => void;
    onRemove: () => void;
}> = ({band, index, errors, onChange, onRemove}) => {
    const mine = errors.filter((e) => e.bandIndex === index);
    return (
        <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
                <input
                    className={clsx(inputClass, 'w-32')}
                    aria-label={`Band ${index + 1} label`}
                    value={band.label}
                    onChange={(e) => onChange({...band, label: e.target.value})}
                    placeholder="Label"
                />
                <input
                    className={clsx(inputClass, 'w-24')}
                    aria-label={`Band ${index + 1} minimum`}
                    type="text"
                    inputMode="decimal"
                    value={band.min_value ?? ''}
                    placeholder="−∞"
                    onChange={(e) =>
                        onChange({...band, min_value: e.target.value === '' ? null : Number(e.target.value)})
                    }
                />
                <span className="text-xs text-gray-500">to</span>
                <input
                    className={clsx(inputClass, 'w-24')}
                    aria-label={`Band ${index + 1} maximum`}
                    type="text"
                    inputMode="decimal"
                    value={band.max_value ?? ''}
                    placeholder="+∞"
                    onChange={(e) =>
                        onChange({...band, max_value: e.target.value === '' ? null : Number(e.target.value)})
                    }
                />
                {/* Severity drives the abnormal count; colour is chosen
                    separately so two bands can share a severity (spec D7). */}
                <select
                    className={inputClass}
                    aria-label={`Band ${index + 1} severity`}
                    value={band.severity}
                    onChange={(e) => onChange({...band, severity: e.target.value as DisplayBand['severity']})}
                    title="Counts toward abnormal results">
                    <option value="normal">normal — not flagged</option>
                    <option value="borderline">borderline — flagged</option>
                    <option value="critical">critical — flagged</option>
                </select>
                <select
                    className={inputClass}
                    aria-label={`Band ${index + 1} colour`}
                    value={band.colour_key ?? ''}
                    onChange={(e) =>
                        onChange({
                            ...band,
                            colour_key: e.target.value === '' ? null : (e.target.value as BandColourKey),
                        })
                    }
                    title="Rendered colour">
                    <option value="">match severity</option>
                    <option value="normal">green</option>
                    <option value="optimal_deep">green (darker)</option>
                    <option value="borderline">orange</option>
                    <option value="critical">red</option>
                </select>
                <span
                    className="inline-block h-4 w-4 shrink-0 rounded-full border border-gray-300"
                    style={{backgroundColor: bandColour(band)}}
                    aria-hidden="true"
                />
                <input
                    className={clsx(inputClass, 'w-20')}
                    aria-label={`Band ${index + 1} proportion`}
                    type="text"
                    inputMode="decimal"
                    value={band.proportion}
                    onChange={(e) => onChange({...band, proportion: Number(e.target.value)})}
                />
                <span className="text-xs text-gray-500">%</span>
                <button
                    type="button"
                    onClick={onRemove}
                    className="ml-auto rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-mld-tertiary">
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
            {mine.map((e, i) => (
                <p key={`${e.rule}-${i}`} className="text-xs font-medium text-mld-tertiary">
                    {e.message}
                </p>
            ))}
        </div>
    );
};

/** Editable form state. Numeric-adjacent and list fields are kept as plain
 *  strings while editing so the input can hold intermediate/invalid typing
 *  (a trailing comma in aliases, an empty LOINC) without fighting React. */
interface RuleDraft {
    id: number | null;
    display_name: string;
    loinc: string;
    assay_code: string;
    name_aliases: string;
    category: string;
    sex: RuleSex;
    display_mode: DisplayMode;
    reference_text: string;
    units: string;
    is_active: boolean;
    notes: string;
    bands: DisplayBand[];
}

function emptyBand(sortOrder: number): DisplayBand {
    return {
        label: '',
        min_value: null,
        max_value: null,
        severity: 'normal',
        colour_key: null,
        proportion: 0,
        sort_order: sortOrder,
    };
}

function emptyDraft(): RuleDraft {
    return {
        id: null,
        display_name: '',
        loinc: '',
        assay_code: '',
        name_aliases: '',
        category: 'other',
        sex: 'any',
        display_mode: 'banded',
        reference_text: '',
        units: '',
        is_active: true,
        notes: '',
        bands: [
            {
                label: '',
                min_value: null,
                max_value: 0,
                severity: 'normal',
                colour_key: null,
                proportion: 50,
                sort_order: 0,
            },
            {
                label: '',
                min_value: 0,
                max_value: null,
                severity: 'normal',
                colour_key: null,
                proportion: 50,
                sort_order: 1,
            },
        ],
    };
}

function ruleToDraft(rule: BiomarkerDisplayRule): RuleDraft {
    return {
        id: rule.id,
        display_name: rule.display_name,
        loinc: rule.loinc ?? '',
        assay_code: rule.assay_code ?? '',
        name_aliases: rule.name_aliases.join(', '),
        category: rule.category ?? 'other',
        sex: rule.sex,
        display_mode: rule.display_mode,
        reference_text: rule.reference_text ?? '',
        units: rule.units ?? '',
        is_active: rule.is_active,
        notes: rule.notes ?? '',
        bands: rule.bands.map((b) => ({...b})),
    };
}

/** A Needs-mapping suggestion as an editor draft. Value-only suggestions keep
 *  the empty draft's starter bands so switching to banded has something to edit. */
function suggestionToDraft(s: UnmappedAnalyteSuggestion, notes: string | null): RuleDraft {
    const blank = emptyDraft();
    return {
        ...blank,
        display_name: s.display_name,
        loinc: s.loinc,
        category: s.category,
        display_mode: s.display_mode,
        reference_text: s.reference_text ?? '',
        units: s.units ?? '',
        notes: notes ?? '',
        bands: s.bands.length > 0 ? s.bands.map((b) => ({...b})) : blank.bands,
    };
}

function parseAliases(raw: string): string[] {
    return raw
        .split(',')
        .map((a) => a.trim())
        .filter((a) => a.length > 0);
}

function draftToInput(draft: RuleDraft, bands: DisplayBand[]): BiomarkerDisplayRuleInput {
    return {
        loinc: draft.loinc.trim() || null,
        assay_code: draft.assay_code.trim() || null,
        name_aliases: parseAliases(draft.name_aliases),
        display_name: draft.display_name.trim(),
        category: draft.category,
        sex: draft.sex,
        display_mode: draft.display_mode,
        reference_text: draft.reference_text.trim() || null,
        units: draft.units.trim() || null,
        is_active: draft.is_active,
        notes: draft.notes.trim() || null,
        bands: draft.display_mode === 'value_only' ? [] : bands,
    };
}

/** Column-2 identity for a list row: whatever actually makes the rule
 *  reachable by the resolver, in priority order. */
function ruleIdentity(rule: BiomarkerDisplayRule): string {
    if (rule.loinc) return `LOINC ${rule.loinc}`;
    if (rule.assay_code) return `Assay ${rule.assay_code}`;
    if (rule.name_aliases.length > 0) {
        const [first, ...rest] = rule.name_aliases;
        return rest.length > 0 ? `Alias: ${first} +${rest.length}` : `Alias: ${first}`;
    }
    return rule.rule_key;
}

/** A 422 carries either a per-band `errors[]` from the shared validator, or a
 *  singular `error` string from the DB CHECK-constraint backstop (a gap in
 *  the validator reaching the database raw). A 409 is a unique-constraint
 *  collision on (rule_key, sex) / (loinc, sex) / (assay_code, sex). Anything
 *  else falls back to whatever message the server supplied. */
function extractServerErrors(error: unknown): {bandErrors: ValidationError[]; message: string | null} {
    const err = error as
        | {status?: number; data?: {errors?: unknown; error?: unknown; message?: unknown}; message?: string}
        | undefined;
    const status = err?.status;
    const data = err?.data;
    // Newer endpoints send {error: machine_code, message: human text} — show the
    // human text, not the code (an admin saw a bare `sex_variant_mismatch` toast).
    const serverText =
        typeof data?.message === 'string' ? data.message : typeof data?.error === 'string' ? data.error : null;
    if (status === 422) {
        if (Array.isArray(data?.errors)) return {bandErrors: data!.errors as ValidationError[], message: null};
        return {bandErrors: [], message: serverText ?? 'One or more bands failed validation.'};
    }
    if (status === 409) {
        return {
            bandErrors: [],
            message: serverText ?? 'A rule with this identity, LOINC, or assay code already exists for this sex.',
        };
    }
    return {bandErrors: [], message: serverText ?? err?.message ?? 'Something went wrong.'};
}

/** `curateLoinc` is the hand-off from the LOINC reference tab's "Curate in
 *  Display rules" link and the Needs mapping tab's "Map it": when set, the
 *  panel scrolls to and highlights the rule(s) for that code, or — when no
 *  rule exists yet — opens the Add-rule editor with the LOINC pre-filled, or
 *  with the whole `curateSuggestion` draft when one came along.
 *  `onCurateConsumed` clears it in the parent so clicking the same code again
 *  re-triggers. */
const BiomarkerDisplayRulesPanel: React.FC<{
    curateLoinc?: string | null;
    curateSuggestion?: UnmappedAnalyteSuggestion | null;
    curateNote?: string | null;
    onCurateConsumed?: () => void;
}> = ({curateLoinc = null, curateSuggestion = null, curateNote = null, onCurateConsumed}) => {
    const queryClient = useQueryClient();
    const [modalOpen, setModalOpen] = React.useState(false);
    const [editingRule, setEditingRule] = React.useState<BiomarkerDisplayRule | null>(null);
    const [draft, setDraft] = React.useState<RuleDraft>(emptyDraft());
    const [serverBandErrors, setServerBandErrors] = React.useState<ValidationError[]>([]);
    const [serverMessage, setServerMessage] = React.useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = React.useState<BiomarkerDisplayRule | null>(null);
    const [highlightLoinc, setHighlightLoinc] = React.useState<string | null>(null);
    // Where a pre-filled draft's name came from; shown as a review prompt.
    const [suggestedFrom, setSuggestedFrom] = React.useState<string | null>(null);

    const listQuery = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => AdminService.listBiomarkerDisplayRules(),
    });
    const rules = listQuery.data?.rules ?? [];

    // Codes already spoken for at the sex being edited. `ux_bdr_loinc_sex` is a
    // partial unique index on (loinc, sex), so a clash is per-sex — and the rule
    // being edited obviously doesn't clash with itself. Filtering here keeps the
    // field a dumb display of what the parent already knows.
    const takenCodes = React.useMemo(
        () =>
            rules
                .filter((r) => r.loinc && r.id !== editingRule?.id && r.sex === draft.sex)
                .map((r) => ({loinc: r.loinc as string, displayName: r.display_name, sex: r.sex})),
        [rules, editingRule?.id, draft.sex]
    );

    const invalidate = () => {
        void queryClient.invalidateQueries({queryKey: QUERY_KEY});
        void queryClient.invalidateQueries({queryKey: UNMAPPED_QUERY_KEY});
    };

    const createMutation = useMutation({
        mutationFn: (input: BiomarkerDisplayRuleInput) => AdminService.createBiomarkerDisplayRule(input),
        onSuccess: () => {
            invalidate();
            toast.success('Rule created');
            closeModal();
        },
        onError: (error) => {
            const {bandErrors, message} = extractServerErrors(error);
            setServerBandErrors(bandErrors);
            setServerMessage(message);
            if (message) toast.error(message);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({id, input}: {id: number; input: BiomarkerDisplayRuleInput}) =>
            AdminService.updateBiomarkerDisplayRule(id, input),
        onSuccess: () => {
            invalidate();
            toast.success('Rule updated');
            closeModal();
        },
        onError: (error) => {
            const {bandErrors, message} = extractServerErrors(error);
            setServerBandErrors(bandErrors);
            setServerMessage(message);
            if (message) toast.error(message);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => AdminService.deleteBiomarkerDisplayRule(id),
        onSuccess: () => {
            invalidate();
            toast.success('Rule deleted');
            setDeleteTarget(null);
        },
        onError: (error) => {
            const {message} = extractServerErrors(error);
            toast.error(message ?? 'Failed to delete rule');
        },
    });

    const closeModal = React.useCallback(() => {
        setModalOpen(false);
        setEditingRule(null);
        setDraft(emptyDraft());
        setSuggestedFrom(null);
        setServerBandErrors([]);
        setServerMessage(null);
    }, []);

    const openCreate = React.useCallback(() => {
        setEditingRule(null);
        setDraft(emptyDraft());
        setSuggestedFrom(null);
        setServerBandErrors([]);
        setServerMessage(null);
        setModalOpen(true);
    }, []);

    const openEdit = React.useCallback((rule: BiomarkerDisplayRule) => {
        setEditingRule(rule);
        setDraft(ruleToDraft(rule));
        setSuggestedFrom(null);
        setServerBandErrors([]);
        setServerMessage(null);
        setModalOpen(true);
    }, []);

    // Scroll the first highlighted row into view. A ref callback (not an
    // effect) because the rows don't exist until the list renders.
    const scrolledForRef = React.useRef<string | null>(null);
    const highlightRowRef = React.useCallback(
        (el: HTMLTableRowElement | null) => {
            if (!el || !highlightLoinc || scrolledForRef.current === highlightLoinc) return;
            scrolledForRef.current = highlightLoinc;
            el.scrollIntoView?.({block: 'center', behavior: 'smooth'});
        },
        [highlightLoinc]
    );

    // Act on a curate hand-off only once the list is loaded — until then we
    // can't know whether the code already has a rule.
    React.useEffect(() => {
        if (!curateLoinc || !listQuery.data) return;
        const hasRule = listQuery.data.rules.some((r) => r.loinc === curateLoinc);
        if (hasRule) {
            scrolledForRef.current = null; // re-scroll even for a repeat of the same code
            setHighlightLoinc(curateLoinc);
        } else {
            setEditingRule(null);
            setDraft(
                curateSuggestion
                    ? suggestionToDraft({...curateSuggestion, loinc: curateLoinc}, curateNote)
                    : {...emptyDraft(), loinc: curateLoinc}
            );
            setSuggestedFrom(curateSuggestion?.nameSource ?? null);
            setServerBandErrors([]);
            setServerMessage(null);
            setModalOpen(true);
        }
        onCurateConsumed?.();
    }, [curateLoinc, curateSuggestion, curateNote, listQuery.data, onCurateConsumed]);

    React.useEffect(() => {
        if (!highlightLoinc) return;
        const timer = setTimeout(() => setHighlightLoinc(null), 6000);
        return () => clearTimeout(timer);
    }, [highlightLoinc]);

    // Re-index sort_order to match on-screen order — bands are only ever
    // added/removed, never dragged, so position IS intent.
    const draftBands = React.useMemo(() => draft.bands.map((b, i) => ({...b, sort_order: i})), [draft.bands]);
    const referenceTextOrNull = draft.reference_text.trim() === '' ? null : draft.reference_text;
    const clientErrors = useBandErrors(draft.display_mode, referenceTextOrNull, draftBands);
    const ruleLevelErrors = [...clientErrors, ...serverBandErrors].filter((e) => e.bandIndex === null);

    const previewRule: BiomarkerDisplayRule = {
        id: draft.id ?? 0,
        rule_key: editingRule?.rule_key ?? '',
        loinc: draft.loinc.trim() || null,
        assay_code: draft.assay_code.trim() || null,
        name_aliases: parseAliases(draft.name_aliases),
        display_name: draft.display_name.trim() || '(untitled rule)',
        sex: draft.sex,
        display_mode: draft.display_mode,
        reference_text: referenceTextOrNull,
        units: draft.units.trim() || null,
        is_active: draft.is_active,
        notes: draft.notes.trim() || null,
        bands: draftBands,
    };

    const updateBand = (index: number, next: DisplayBand) => {
        setDraft((d) => ({...d, bands: d.bands.map((b, i) => (i === index ? next : b))}));
        setServerBandErrors([]);
    };
    const removeBand = (index: number) => {
        setDraft((d) => ({...d, bands: d.bands.filter((_, i) => i !== index)}));
        setServerBandErrors([]);
    };
    const addBand = () => {
        setDraft((d) => ({...d, bands: [...d.bands, emptyBand(d.bands.length)]}));
        setServerBandErrors([]);
    };

    const handleSave = () => {
        if (!draft.display_name.trim()) {
            toast.error('Display name is required');
            return;
        }
        if (!draft.loinc.trim() && !draft.assay_code.trim() && parseAliases(draft.name_aliases).length === 0) {
            toast.error('A rule needs a LOINC, an assay code, or at least one alias to be reachable');
            return;
        }
        const input = draftToInput(draft, draftBands);
        if (editingRule) {
            updateMutation.mutate({id: editingRule.id, input});
        } else {
            createMutation.mutate(input);
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="space-y-6">
            <div className="rounded-lg border border-mld-panel-border bg-mld-menu-background p-6 shadow-sm">
                <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-mld-panel-border pb-4">
                    <div>
                        <h2 className="text-xl font-semibold text-mld-text-primary">Biomarker Display Rules</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            These bands decide the colour, label and chart position every patient sees for a biomarker.
                            Inactive rules stay listed below as a review queue — they are never hidden.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={openCreate}
                        className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-mld-primary-700">
                        <Plus className="h-4 w-4" />
                        Add rule
                    </button>
                </div>

                <div className="overflow-hidden rounded-lg border border-mld-panel-border">
                    <div className="max-h-[65vh] overflow-auto">
                        <table className="min-w-full divide-y divide-mld-panel-border text-sm">
                            <thead className="sticky top-0 bg-mld-menu-background text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
                                <tr>
                                    <th className="px-4 py-2">Rule</th>
                                    <th className="px-4 py-2">Key</th>
                                    <th className="px-4 py-2">Sex</th>
                                    <th className="px-4 py-2">Bands</th>
                                    <th className="px-4 py-2">Preview</th>
                                    <th className="w-24 px-4 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-mld-panel-border bg-white">
                                {listQuery.isLoading ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                                            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                                        </td>
                                    </tr>
                                ) : listQuery.isError ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-10 text-center text-red-600">
                                            Unable to load biomarker display rules.
                                        </td>
                                    </tr>
                                ) : rules.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                                            No display rules yet. Add the first one to take a biomarker off the
                                            hardcoded config path.
                                        </td>
                                    </tr>
                                ) : (
                                    rules.map((rule) => (
                                        <tr
                                            key={rule.id}
                                            ref={
                                                highlightLoinc && rule.loinc === highlightLoinc
                                                    ? highlightRowRef
                                                    : undefined
                                            }
                                            data-curated={
                                                highlightLoinc && rule.loinc === highlightLoinc ? true : undefined
                                            }
                                            className={clsx(
                                                'align-top transition-colors duration-700',
                                                highlightLoinc && rule.loinc === highlightLoinc && 'bg-teal-50'
                                            )}>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-mld-text-primary">
                                                    {rule.display_name}
                                                    <span className="ml-2 rounded bg-mld-background px-1.5 py-0.5 text-[10px] font-medium text-mld-muted-text">
                                                        {HEALTH_CATEGORY_LABELS[
                                                            (rule.category ??
                                                                'other') as keyof typeof HEALTH_CATEGORY_LABELS
                                                        ] ?? rule.category}
                                                    </span>
                                                </div>
                                                {/*
                                                 * The only thing on the row that says this rule is switched off,
                                                 * so it has to name the cost rather than the flag. It read
                                                 * "Needs review" in amber, which sounds like a tidy-up task:
                                                 * four thyroid rules sat inactive behind that badge while
                                                 * patients saw the lab's raw LOINC shortname on their result
                                                 * cards ("T3Free SerPl-mCnc" instead of "Free T3").
                                                 */}
                                                {!rule.is_active ? (
                                                    <span
                                                        className="mt-1 inline-block rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800"
                                                        title="This rule is switched off. Until it is turned on, results for this biomarker show patients the lab's raw code instead of this name.">
                                                        Off &mdash; patients see the raw lab code
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs text-mld-text-secondary">
                                                {ruleIdentity(rule)}
                                            </td>
                                            <td className="px-4 py-3 text-mld-text-primary">{SEX_LABEL[rule.sex]}</td>
                                            <td className="px-4 py-3 text-mld-text-primary">
                                                {rule.display_mode === 'value_only'
                                                    ? 'Value only'
                                                    : `${rule.bands.length} band${rule.bands.length === 1 ? '' : 's'}`}
                                            </td>
                                            <td className="px-4 py-3">
                                                <BandPreview rule={rule} />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => openEdit(rule)}
                                                        className="rounded-md p-1.5 text-mld-muted-text hover:bg-mld-background hover:text-mld-text-secondary"
                                                        title="Edit"
                                                        aria-label={`Edit ${rule.display_name}`}>
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setDeleteTarget(rule)}
                                                        className="rounded-md p-1.5 text-mld-muted-text hover:bg-red-50 hover:text-mld-tertiary"
                                                        title="Delete"
                                                        aria-label={`Delete ${rule.display_name}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Create / Edit Modal */}
            <Transition appear show={modalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closeModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-2xl rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
                                        <div>
                                            <Dialog.Title className="text-lg font-semibold text-mld-text-primary">
                                                {editingRule ? `Edit ${editingRule.display_name}` : 'Add Rule'}
                                            </Dialog.Title>
                                            <p className="text-xs text-gray-500">
                                                Bands must fully cover the value range with no gap and no overlap.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                            aria-label="Close">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>

                                    <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
                                        {suggestedFrom ? (
                                            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                                Pre-filled suggestion &mdash; name from the {suggestedFrom}, bands from
                                                the lab&rsquo;s reference range where one exists. Check the name,
                                                category and bands before saving; nothing is saved until you do.
                                            </p>
                                        ) : null}
                                        {serverMessage ? (
                                            <p className="rounded-md border border-mld-tertiary/30 bg-mld-tertiary/5 px-3 py-2 text-sm font-medium text-mld-tertiary">
                                                {serverMessage}
                                            </p>
                                        ) : null}

                                        <div className="grid grid-cols-2 gap-4">
                                            <label className="block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Display name
                                                </span>
                                                <input
                                                    className={clsx(inputClass, 'w-full')}
                                                    value={draft.display_name}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({...d, display_name: e.target.value}))
                                                    }
                                                    placeholder="e.g. HDL"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Sex
                                                </span>
                                                <select
                                                    className={clsx(inputClass, 'w-full')}
                                                    value={draft.sex}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({...d, sex: e.target.value as RuleSex}))
                                                    }>
                                                    {SEX_OPTIONS.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            <LoincLookupField
                                                value={draft.loinc}
                                                onChange={(loinc) => setDraft((d) => ({...d, loinc}))}
                                                takenCodes={takenCodes}
                                            />
                                            <label className="block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Assay code
                                                </span>
                                                <input
                                                    className={clsx(inputClass, 'w-full')}
                                                    value={draft.assay_code}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({...d, assay_code: e.target.value}))
                                                    }
                                                />
                                            </label>
                                            <label className="col-span-2 block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Aliases{' '}
                                                    <span className="font-normal text-gray-500">
                                                        (comma-separated, used when no LOINC/assay code matches)
                                                    </span>
                                                </span>
                                                <input
                                                    className={clsx(inputClass, 'w-full')}
                                                    value={draft.name_aliases}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({...d, name_aliases: e.target.value}))
                                                    }
                                                    placeholder="hdl, hdl-c, direct hdl"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Units
                                                </span>
                                                <input
                                                    className={clsx(inputClass, 'w-full')}
                                                    value={draft.units}
                                                    onChange={(e) => setDraft((d) => ({...d, units: e.target.value}))}
                                                    placeholder="mg/dL"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Display mode
                                                </span>
                                                <select
                                                    className={clsx(inputClass, 'w-full')}
                                                    value={draft.display_mode}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({
                                                            ...d,
                                                            display_mode: e.target.value as DisplayMode,
                                                        }))
                                                    }>
                                                    {MODE_OPTIONS.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Health category
                                                </span>
                                                <select
                                                    aria-label="Health category"
                                                    className={clsx(inputClass, 'w-full')}
                                                    value={draft.category}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({...d, category: e.target.value}))
                                                    }>
                                                    {CATEGORY_OPTIONS.map(([value, label]) => (
                                                        <option key={value} value={value}>
                                                            {label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="col-span-2 flex items-start gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={draft.is_active}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({...d, is_active: e.target.checked}))
                                                    }
                                                    className="h-4 w-4 rounded border-gray-300 text-mld-primary focus:ring-mld-primary"
                                                />
                                                <span className="text-sm text-mld-text-primary">
                                                    Show this name to patients
                                                    <span className="mt-0.5 block text-xs text-mld-text-secondary">
                                                        Leave this off and patients see the lab&rsquo;s raw code on
                                                        their result card, such as &ldquo;T3Free SerPl-mCnc&rdquo;
                                                        instead of &ldquo;Free T3&rdquo;. The rule is kept either way,
                                                        never deleted.
                                                    </span>
                                                </span>
                                            </label>
                                            <label className="col-span-2 block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Notes
                                                </span>
                                                <textarea
                                                    className={clsx(inputClass, 'w-full')}
                                                    rows={2}
                                                    value={draft.notes}
                                                    onChange={(e) => setDraft((d) => ({...d, notes: e.target.value}))}
                                                />
                                            </label>
                                        </div>

                                        {ruleLevelErrors.length > 0 ? (
                                            <div className="space-y-1 rounded-md border border-mld-tertiary/30 bg-mld-tertiary/5 px-3 py-2">
                                                {ruleLevelErrors.map((e, i) => (
                                                    <p
                                                        key={`${e.rule}-${i}`}
                                                        className="text-xs font-medium text-mld-tertiary">
                                                        {e.message}
                                                    </p>
                                                ))}
                                            </div>
                                        ) : null}

                                        {draft.display_mode === 'value_only' ? (
                                            <label className="block">
                                                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">
                                                    Reference text
                                                </span>
                                                <input
                                                    className={clsx(inputClass, 'w-full')}
                                                    value={draft.reference_text}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({...d, reference_text: e.target.value}))
                                                    }
                                                    placeholder="e.g. Negative"
                                                />
                                            </label>
                                        ) : (
                                            <div>
                                                <div className="mb-2 flex items-center justify-between">
                                                    <span className="text-xs font-medium text-mld-text-secondary">
                                                        Bands
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={addBand}
                                                        className="inline-flex items-center gap-1 text-xs font-medium text-mld-primary hover:underline">
                                                        <Plus className="h-3 w-3" />
                                                        Add band
                                                    </button>
                                                </div>

                                                <div className="mb-3">
                                                    <BandPreview rule={previewRule} />
                                                </div>

                                                <div className="space-y-3">
                                                    {draftBands.map((band, index) => (
                                                        <BandRow
                                                            key={index}
                                                            band={band}
                                                            index={index}
                                                            errors={[...clientErrors, ...serverBandErrors]}
                                                            onChange={(next) => updateBand(index, next)}
                                                            onRemove={() => removeBand(index)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSave}
                                            disabled={isSaving || clientErrors.length > 0}
                                            className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-mld-primary-700 disabled:cursor-not-allowed disabled:opacity-60">
                                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                            {editingRule ? 'Save changes' : 'Create rule'}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Delete Confirmation Modal */}
            <Transition appear show={deleteTarget != null} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setDeleteTarget(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center px-4 py-8">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-sm rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                    <div className="px-6 py-5">
                                        <Dialog.Title className="text-base font-semibold text-mld-text-primary">
                                            Delete rule
                                        </Dialog.Title>
                                        <p className="mt-2 text-sm text-gray-500">
                                            {deleteTarget
                                                ? `"${deleteTarget.display_name}" will stop resolving for patients immediately. This cannot be undone.`
                                                : ''}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={() => setDeleteTarget(null)}
                                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                                            disabled={deleteMutation.isPending}
                                            className="inline-flex items-center justify-center gap-2 rounded-md bg-rose-500 px-4 py-2 text-sm font-medium text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60">
                                            {deleteMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : null}
                                            Delete
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default BiomarkerDisplayRulesPanel;
